import Vue from 'vue'
import { createPopper } from '@popperjs/core'

const tooltips = []

const ATS_COLUMN_TOOLTIP_THEME = 'ats-column'

function normalizeTooltipOptions(binding) {
	const value = binding.value

	if (!value) {
		return null
	}

	if (typeof value === 'object') {
		const items = Array.isArray(value.items)
			? value.items.filter(Boolean).map(String)
			: []
		const content = value.content || value.text || items.join('\n')

		return content || value.title
			? {
					content,
					items,
					title: value.title || '',
					theme: value.theme || null,
					placement: value.placement || binding.arg || 'top'
			  }
			: null
	}

	return {
		content: value,
		items: [],
		title: '',
		theme: null,
		placement: binding.arg || 'top'
	}
}

function isAtsColumnTooltip(options) {
	return options?.theme === ATS_COLUMN_TOOLTIP_THEME
}

function findTooltipReferenceElement(el, options) {
	if (!isAtsColumnTooltip(options)) {
		return el
	}

	return el.querySelector('[data-tooltip-anchor]') || el
}

function renderTooltipBody(tooltipBody, options) {
	while (tooltipBody.firstChild) {
		tooltipBody.removeChild(tooltipBody.firstChild)
	}

	if (options?.items?.length) {
		options.items.forEach(item => {
			const itemElement = document.createElement('div')
			itemElement.textContent = item
			tooltipBody.appendChild(itemElement)
		})

		return
	}

	tooltipBody.textContent = options?.content || ''
}

function applyTooltipTheme(
	tooltip,
	tooltipTitle,
	tooltipBody,
	arrow,
	arrowInner,
	options
) {
	tooltip.style.display = 'none'
	tooltip.style.maxWidth = '22rem'
	tooltip.style.width = 'fit-content'
	tooltip.style.minWidth = '0'

	tooltipTitle.textContent = options?.title || ''
	tooltipTitle.style.display = options?.title ? 'block' : 'none'
	tooltipTitle.style.whiteSpace = 'normal'
	tooltipTitle.style.wordBreak = 'break-word'

	tooltipBody.className = 'px-3 py-1 leading-snug'
	tooltipBody.style.padding = '0.25rem 0.75rem'
	tooltipBody.style.whiteSpace = 'normal'
	tooltipBody.style.wordBreak = 'break-word'
	tooltipBody.style.display = options?.content ? 'block' : 'none'

	if (!isAtsColumnTooltip(options)) {
		tooltip.className =
			'absolute z-50 text-sm text-white bg-black rounded opacity-0 pointer-events-none'
		tooltip.style.backgroundColor = ''
		tooltip.style.border = ''
		tooltip.style.borderRadius = ''
		tooltip.style.boxShadow = ''
		tooltip.style.color = ''
		tooltip.style.fontSize = ''
		tooltip.style.lineHeight = ''
		tooltip.style.opacity = ''
		tooltip.style.transition = ''
		tooltipTitle.className = 'px-3 pt-1 font-semibold leading-snug'
		tooltipTitle.style.padding = options?.content
			? '0.25rem 0.75rem 0'
			: '0.25rem 0.75rem'
		tooltipBody.className = 'px-3 py-1 leading-snug'
		tooltipBody.style.padding = '0.25rem 0.75rem'

		if (arrow) {
			arrow.style.display = 'none'
		}

		if (arrowInner) {
			arrowInner.className = ''
			arrowInner.style.display = 'none'
			arrowInner.style.zIndex = ''
		}

		return
	}

	tooltip.className =
		'absolute z-50 text-sm rounded shadow-sm pointer-events-none'
	tooltip.style.backgroundColor = '#ffffff'
	tooltip.style.border = '1px solid rgba(229, 231, 235, 0.9)'
	tooltip.style.borderRadius = '0.25rem'
	tooltip.style.color = '#111827'
	tooltip.style.fontSize = '0.875rem'
	tooltip.style.lineHeight = '1.25rem'
	tooltip.style.opacity = '0'
	tooltip.style.transition = 'opacity 120ms ease'
	tooltipTitle.className = 'px-3 pt-1.5 font-semibold leading-snug'
	tooltipTitle.style.padding = options?.content
		? '0.375rem 0.75rem 0'
		: '0.375rem 0.75rem'
	tooltipBody.className = 'px-3 py-1.5 leading-snug'
	tooltipBody.style.padding = '0.375rem 0.75rem'

	if (arrow) {
		arrow.className = ''
		arrow.style.display = 'block'
		arrow.style.position = 'absolute'
		arrow.style.width = '18px'
		arrow.style.height = '12px'
		arrow.style.backgroundColor = 'transparent'
		arrow.style.overflow = 'hidden'
	}

	if (arrowInner) {
		arrowInner.className = ''
		arrowInner.style.display = 'block'
		arrowInner.style.position = 'absolute'
		arrowInner.style.width = '12px'
		arrowInner.style.height = '12px'
		arrowInner.style.margin = '0'
		arrowInner.style.backgroundColor = '#ffffff'
		arrowInner.style.border = '1px solid rgba(229, 231, 235, 0.9)'
		arrowInner.style.boxShadow = '0 1px 2px 0 rgba(0, 0, 0, 0.05)'
		arrowInner.style.clipPath = ''
		arrowInner.style.transform = 'rotate(45deg)'
		arrowInner.style.zIndex = ''
	}
}

function setTooltipVisible(tooltip, options, visible) {
	if (isAtsColumnTooltip(options)) {
		tooltip.style.opacity = visible ? '1' : '0'
		return
	}

	if (visible) {
		tooltip.classList.remove('opacity-0')
		tooltip.classList.add('opacity-90')
		return
	}

	tooltip.classList.remove('opacity-90')
	tooltip.classList.add('opacity-0')
}

function applyArrowPlacement({ state }) {
	const arrow = state.elements.arrow

	if (!arrow) {
		return
	}

	const placement = state.placement.split('-')[0]
	const arrowInner = arrow.firstElementChild

	if (placement === 'top') {
		arrow.style.top = ''
		arrow.style.right = ''
		arrow.style.bottom = '-12px'
		arrow.style.marginTop = ''
		arrow.style.width = '18px'
		arrow.style.height = '12px'

		if (arrowInner) {
			arrowInner.style.left = '3px'
			arrowInner.style.top = '-6px'
			arrowInner.style.width = '12px'
			arrowInner.style.height = '12px'
			arrowInner.style.transform = 'rotate(45deg)'
		}
	} else if (placement === 'bottom') {
		arrow.style.right = ''
		arrow.style.bottom = ''
		arrow.style.top = '-12px'
		arrow.style.marginTop = ''
		arrow.style.width = '18px'
		arrow.style.height = '12px'

		if (arrowInner) {
			arrowInner.style.left = '3px'
			arrowInner.style.top = '6px'
			arrowInner.style.width = '12px'
			arrowInner.style.height = '12px'
			arrowInner.style.transform = 'rotate(45deg)'
		}
	} else if (placement === 'left') {
		arrow.style.bottom = ''
		arrow.style.left = ''
		arrow.style.right = '-12px'
		arrow.style.marginTop = '-3px'
		arrow.style.width = '12px'
		arrow.style.height = '18px'

		if (arrowInner) {
			arrowInner.style.left = '-6px'
			arrowInner.style.top = '3px'
			arrowInner.style.width = '12px'
			arrowInner.style.height = '12px'
			arrowInner.style.transform = 'rotate(45deg)'
		}
	} else if (placement === 'right') {
		arrow.style.right = ''
		arrow.style.bottom = ''
		arrow.style.left = '-12px'
		arrow.style.marginTop = '-3px'
		arrow.style.width = '12px'
		arrow.style.height = '18px'

		if (arrowInner) {
			arrowInner.style.left = '6px'
			arrowInner.style.top = '3px'
			arrowInner.style.width = '12px'
			arrowInner.style.height = '12px'
			arrowInner.style.transform = 'rotate(45deg)'
		}
	}
}

function registerTooltip(config) {
	config.handlers = {
		mouseenter: () => config.open(),
		focus: () => config.open(),
		mouseleave: () => config.close(),
		blur: () => config.close(),
		click: () => config.close()
	}

	Object.entries(config.handlers).forEach(([event, handler]) => {
		config.el.addEventListener(event, handler)
	})

	tooltips.push(config)
}

function unregisterTooltip(el) {
	const index = tooltips.findIndex(tooltip => tooltip.el === el)
	const config = tooltips[index]

	if (config) {
		Object.entries(config.handlers).forEach(([event, handler]) => {
			el.removeEventListener(event, handler)
		})

		if (config.tooltip.parentNode) {
			config.tooltip.parentNode.removeChild(config.tooltip)
		}

		tooltips.splice(index, 1)
	}
}

Vue.directive('tooltip', {
	inserted(el, binding) {
		// Don't create tooltip if value is falsy
		const options = normalizeTooltipOptions(binding)
		if (!options) return

		const tooltip = document.createElement('div')

		// Create tooltip body - use textContent for XSS safety
		// Only translation strings and trusted content should be passed to this directive
		const tooltipTitle = document.createElement('div')
		tooltipTitle.textContent = options.title
		tooltip.appendChild(tooltipTitle)

		const tooltipBody = document.createElement('div')
		renderTooltipBody(tooltipBody, options)
		tooltip.appendChild(tooltipBody)

		const arrow = document.createElement('div')
		arrow.setAttribute('data-popper-arrow', '')
		const arrowInner = document.createElement('div')
		arrow.appendChild(arrowInner)
		tooltip.appendChild(arrow)

		applyTooltipTheme(
			tooltip,
			tooltipTitle,
			tooltipBody,
			arrow,
			arrowInner,
			options
		)
		document.body.appendChild(tooltip)

		const config = {
			el,
			tooltip,
			tooltipTitle,
			tooltipBody,
			arrow,
			arrowInner,
			options,
			popper: null,
			open() {
				if (!this.options) {
					return
				}

				this.tooltip.style.display = 'block'

				const modifiers = [
					{
						name: 'offset',
						options: {
							offset: [0, isAtsColumnTooltip(this.options) ? 10 : 8]
						}
					}
				]

				if (isAtsColumnTooltip(this.options)) {
					modifiers.push(
						{
							name: 'arrow',
							options: {
								element: this.arrow,
								padding: 8
							}
						},
						{
							name: 'applyArrowPlacement',
							enabled: true,
							phase: 'write',
							fn: applyArrowPlacement
						}
					)
				}

				this.popper = createPopper(
					findTooltipReferenceElement(this.el, this.options),
					this.tooltip,
					{
						placement: this.options.placement,
						modifiers
					}
				)

				requestAnimationFrame(() => {
					setTooltipVisible(this.tooltip, this.options, true)
				})
			},
			close() {
				if (!config.options) {
					config.tooltip.style.display = 'none'
					return
				}

				setTooltipVisible(config.tooltip, config.options, false)

				if (config.popper) {
					config.popper.destroy()
					config.popper = null
				}

				config.tooltip.style.display = 'none'
			}
		}

		registerTooltip(config)
	},

	update(el, binding) {
		// Find the existing tooltip config
		const config = tooltips.find(t => t.el === el)
		if (config && config.tooltip) {
			const options = normalizeTooltipOptions(binding)

			if (!options) {
				// Hide tooltip if value is falsy
				config.tooltip.style.display = 'none'
				config.options = null
				if (config.popper) {
					config.popper.destroy()
					config.popper = null
				}
			} else {
				// Update the tooltip content - use textContent for XSS safety
				const isOpen = Boolean(config.popper)
				config.options = options
				config.tooltipTitle.textContent = options.title
				renderTooltipBody(config.tooltipBody, options)
				applyTooltipTheme(
					config.tooltip,
					config.tooltipTitle,
					config.tooltipBody,
					config.arrow,
					config.arrowInner,
					options
				)

				if (isOpen) {
					config.tooltip.style.display = 'block'
					setTooltipVisible(config.tooltip, options, true)
					config.popper.update()
				}
			}
		}
	},

	unbind(el, binding) {
		unregisterTooltip(el)
	}
})
